/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box } from '@mui/material';
import type { FC } from 'react';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CustomTooltip } from '@opengeoweb/shared';
import type { CoreAppStore } from '@opengeoweb/store';
import { layerActions, layerSelectors, layerTypes } from '@opengeoweb/store';
import { AcceptanceTime } from './AcceptanceTime';
import { useCoreTranslation } from '../../../../../utils/i18n';

export const AcceptanceTimeConnect: FC<{
  layerId: string | undefined;
  icon?: React.ElementType;
  tooltipTitle?: string;
  skipLocalStyling?: boolean;
  mapId: string;
}> = ({ layerId, icon, tooltipTitle, skipLocalStyling, mapId }) => {
  const { t } = useCoreTranslation();
  const dispatch = useDispatch();
  const isEnabled = useSelector((store: CoreAppStore) =>
    layerSelectors.getLayerEnabled(store, layerId),
  );
  const acceptanceTimeInMinutes = useSelector((store: CoreAppStore) => {
    return layerSelectors.getAcceptanceTimeInMinutes(store, layerId);
  });

  const onChangeAcceptanceTime = (acceptanceTime: number | null): void => {
    dispatch(
      layerActions.layerChangeAcceptanceTime({
        layerId: layerId!,
        acceptanceTime,
        mapId,
        origin: layerTypes.LayerActionOrigin.layerManager,
      }),
    );
  };

  const layerHasTimeDimension = useSelector((store: CoreAppStore) =>
    layerSelectors.getLayerHasTimeDimension(store, layerId),
  );
  if (!layerHasTimeDimension) {
    return (
      <CustomTooltip placement="top" title={t('layermanager-dimensions-none')}>
        <Box data-testid="noTimeDimension" sx={{ height: 32, width: '100%' }} />
      </CustomTooltip>
    );
  }
  return (
    <AcceptanceTime
      acceptanceTimeInMinutes={acceptanceTimeInMinutes}
      icon={icon}
      isEnabled={isEnabled}
      onChangeAcceptanceTime={onChangeAcceptanceTime}
      skipLocalStyling={skipLocalStyling}
      tooltipTitle={tooltipTitle}
    />
  );
};
